import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import { z } from 'zod';
import { authApi } from '@/api';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { platformApi, type OrganizationFeatureDto } from '@/api/platform.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useWorkspaceModule } from '@/hooks/useWorkspaceModule';
import { featuresForModule, moduleAdminPath, moduleSubscriptionLink } from '@/config/moduleWorkspace';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { setTheme } from '@/store/appSlice';
import { useEffect, useState } from 'react';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { SampleDataPanel } from '@/components/ui/SampleDataPanel';

const schema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Must match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function ModuleWorkspaceSettingsPage() {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.app.theme);
  const { module, moduleLabel, ready } = useWorkspaceModule();
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState<OrganizationFeatureDto[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const homePath = module ? (getModuleEntryByErpModule(module)?.homePath ?? '/org') : '/org';
  const moduleFeatures = module ? featuresForModule(features, module) : [];

  useEffect(() => {
    if (!module) return;
    setFeaturesLoading(true);
    platformApi
      .listFeatures()
      .then(setFeatures)
      .catch(() => undefined)
      .finally(() => setFeaturesLoading(false));
  }, [module]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      notify.success('Password changed');
      reset();
    } catch (err) {
      showApiError(err, 'Failed');
    } finally {
      setLoading(false);
    }
  };

  if (!ready || !module) {
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }

  return (
    <div>
      <PageHeader
        title={`${moduleLabel} settings`}
        description={`Workspace preferences and feature access for ${moduleLabel}. Plans are managed under Subscription.`}
        breadcrumbs={[
          { label: moduleLabel, to: homePath },
          { label: 'Settings' },
        ]}
      />
      <SampleDataPanel className="mb-6 max-w-lg" />
      <div className="card mb-6 max-w-lg p-6">
        <h3 className="font-semibold">Appearance</h3>
        <div className="mt-4 flex gap-2">
          <Button
            variant={theme === 'light' ? 'primary' : 'secondary'}
            onClick={() => dispatch(setTheme('light'))}
          >
            Light
          </Button>
          <Button
            variant={theme === 'dark' ? 'primary' : 'secondary'}
            onClick={() => dispatch(setTheme('dark'))}
          >
            Dark
          </Button>
        </div>
      </div>
      <div className="card mb-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">{moduleLabel} features</h3>
          <Link
            to={moduleSubscriptionLink(module)}
            className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Manage subscription
          </Link>
        </div>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Capabilities included with your current {moduleLabel} plan. Upgrade subscription to unlock more.
        </p>
        {featuresLoading ? (
          <p className="mt-4 text-sm text-slate-500">Loading features…</p>
        ) : moduleFeatures.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No feature flags for this module yet.</p>
        ) : (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {moduleFeatures.map((feature) => (
              <div
                key={feature.code}
                className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"
              >
                <p className="text-sm font-medium">{feature.name}</p>
                <p className="text-xs text-slate-500">{feature.code}</p>
                <p
                  className={`mt-1 text-xs font-medium ${
                    feature.enabled
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}
                >
                  {feature.enabled ? 'Included in plan' : feature.locked ? 'Upgrade plan to unlock' : 'Not available'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="card mb-6 max-w-lg p-6">
        <h3 className="font-semibold">Organization profile</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Company name, branding, and contact details shared across modules.
        </p>
        <Link
          to={module ? moduleAdminPath(module, 'profile') : '/org/profile'}
          className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline"
        >
          Open organization profile
        </Link>
      </div>
      <div className="card max-w-lg p-6">
        <h3 className="font-semibold">Change password</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
          <Input label="Current" type="password" {...register('currentPassword')} error={errors.currentPassword?.message} />
          <Input label="New" type="password" {...register('newPassword')} error={errors.newPassword?.message} />
          <Input label="Confirm" type="password" {...register('confirmPassword')} error={errors.confirmPassword?.message} />
          <Button type="submit" loading={loading}>Update</Button>
        </form>
      </div>
    </div>
  );
}
